import { useState, useEffect, useCallback, useMemo } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { TrendingUp } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function formatTime(isoStr) {
  try {
    const d = new Date(isoStr);
    return d.toLocaleTimeString('en-US', {
      timeZone: 'America/New_York',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return '';
  }
}

function CustomTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const { avg_score, count, timestamp } = payload[0].payload;
  return (
    <div
      className="rounded-md px-3 py-2 font-mono text-xs"
      style={{ backgroundColor: '#1c2333', border: '1px solid #30363d', color: '#c9d1d9' }}
    >
      <div style={{ color: '#8b949e' }}>{formatTime(timestamp)}</div>
      <div style={{ color: avg_score > 0 ? '#22c55e' : avg_score < 0 ? '#ef4444' : '#8b949e' }}>
        Score: {avg_score > 0 ? '+' : ''}{avg_score}
      </div>
      <div style={{ color: '#484f58' }}>{count} posts</div>
    </div>
  );
}

export default function SentimentTimeline({ refreshKey }) {
  const [timeline, setTimeline] = useState([]);
  const [expanded, setExpanded] = useState(() => {
    try {
      const saved = localStorage.getItem('mf-timeline-expanded');
      return saved === null ? false : saved === 'true';
    } catch {
      return false;
    }
  });
  const [loading, setLoading] = useState(true);

  const toggleExpanded = () => {
    setExpanded(prev => {
      const next = !prev;
      try { localStorage.setItem('mf-timeline-expanded', String(next)); } catch {}
      return next;
    });
  };

  const avgScore = useMemo(() => {
    if (timeline.length === 0) return 0;
    const total = timeline.reduce((sum, t) => sum + (t.avg_score || 0), 0);
    return (total / timeline.length).toFixed(1);
  }, [timeline]);

  const fetchTimeline = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/v1/sentinel/timeline?hours=4&bucket_minutes=10`);
      setTimeline(res.data.timeline || []);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTimeline();
    const interval = setInterval(fetchTimeline, 60000);
    return () => clearInterval(interval);
  }, [fetchTimeline]);

  // Refresh when new posts arrive
  useEffect(() => {
    if (refreshKey > 0) fetchTimeline();
  }, [refreshKey, fetchTimeline]);

  if (loading && timeline.length === 0) return null;

  // Show timeline container even with minimal data
  const hasData = timeline.length >= 2;

  return (
    <div
      data-testid="sentiment-timeline"
      className="mb-3 sm:mb-4 rounded-[10px] border"
      style={{ backgroundColor: '#111827', borderColor: '#30363d' }}
    >
      <div
        className="flex items-center justify-between px-3 sm:px-4 py-2 cursor-pointer select-none"
        onClick={toggleExpanded}
        data-testid="timeline-toggle"
      >
        <div className="flex items-center gap-2">
          <TrendingUp size={13} style={{ color: '#8b949e' }} />
          <span className="font-mono text-[11px] tracking-[0.12em] uppercase font-medium" style={{ color: '#8b949e' }}>
            Sentiment Timeline
          </span>
          <span className="font-mono text-[10px]" style={{ color: '#484f58' }}>4h</span>
          {!expanded && timeline.length >= 2 && (
            <span className="font-mono text-[11px] font-semibold" style={{
              color: avgScore > 0 ? '#22c55e' : avgScore < 0 ? '#ef4444' : '#8b949e',
            }}>
              AVG: {avgScore > 0 ? '+' : ''}{avgScore}
            </span>
          )}
        </div>
        <span className="font-mono text-[10px]" style={{ color: '#484f58' }}>
          {expanded ? '\u25B4 collapse' : '\u25BE expand'}
        </span>
      </div>

      {expanded && (
        <div className="px-2 pb-2" style={{ height: 120 }}>
          {hasData ? (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={timeline} margin={{ top: 4, right: 8, bottom: 0, left: -20 }}>
              <defs>
                <linearGradient id="sentGreen" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#22c55e" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#22c55e" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="sentRed" x1="0" y1="1" x2="0" y2="0">
                  <stop offset="0%" stopColor="#ef4444" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#ef4444" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="timestamp"
                tickFormatter={formatTime}
                tick={{ fill: '#484f58', fontSize: 10, fontFamily: 'monospace' }}
                axisLine={{ stroke: '#21262d' }}
                tickLine={false}
                interval="preserveStartEnd"
                minTickGap={60}
              />
              <YAxis
                domain={[-5, 5]}
                tick={{ fill: '#484f58', fontSize: 10, fontFamily: 'monospace' }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => (v > 0 ? `+${v}` : v)}
              />
              <ReferenceLine y={0} stroke="#30363d" strokeDasharray="3 3" />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="avg_score"
                stroke="#22c55e"
                fill="url(#sentGreen)"
                strokeWidth={1.5}
                dot={false}
                activeDot={{ r: 3, fill: '#22c55e' }}
                baseValue={0}
                isAnimationActive={false}
              />
              <Area
                type="monotone"
                dataKey="avg_score"
                stroke="#ef4444"
                fill="url(#sentRed)"
                strokeWidth={0}
                dot={false}
                baseValue={0}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-full font-mono text-xs" style={{ color: '#484f58' }}>
              Collecting data points...
            </div>
          )}
        </div>
      )}
    </div>
  );
}
